import { IconButton, Stack, TextField, useTheme } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import { ChangeEvent, memo, useEffect, useState } from 'react'
import { PRODUCT_MAX_AMOUNT, TABLE_ROW_HEIGHT } from 'app/constants'
import { IProduct } from 'api/types/product'
import { Row } from '@tanstack/react-table'
import { useDebounce } from 'lib/hooks/use-debounce'
import { useDeleteProduct } from 'api/hooks/use-delete-product'
import { useUser } from 'api/hooks/use-user'
import { useAmount } from 'api/hooks/use-amount'
import { useGlobalLoading } from 'api/hooks/use-global-loading'

type Props = {
  row: Row<IProduct>
}

export const CartItemAmount = memo(({ row }: Props) => {
  const theme = useTheme()
  const { user } = useUser()
  const initialAmount = row.original.amount
  const [amount, setAmount] = useState(initialAmount)
  const debouncedAmount = useDebounce(amount, 200)
  const isLoading = useGlobalLoading()
  const { amountMutation } = useAmount()
  const { deleteProductMutation } = useDeleteProduct()
  const isMinAmount = amount === 0
  const isMaxAmount = amount >= PRODUCT_MAX_AMOUNT

  const iconStyles = {
    fontSize: '13px',
    color: theme.palette.action.disabled,
  }

  useEffect(() => {
    if (initialAmount !== debouncedAmount) {
      if (debouncedAmount === 0) {
        deleteProductMutation({
          id: row.original.id,
          subCategory: row.original.subCategoryId,
        })
      } else {
        amountMutation({
          id: row.original.id,
          subCategory: row.original.subCategoryId,
          amount: debouncedAmount,
          price: row.original.yourPrice,
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedAmount])

  const handleChangeAmount = (e: ChangeEvent<HTMLInputElement>) => {
    if (Number(e.target.value) >= 0) {
      setAmount(Math.max(Number(e.target.value), 0))
    }
  }

  return (
    <Stack
      direction="row"
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        height: `${TABLE_ROW_HEIGHT}px`,
      }}
    >
      <IconButton
        aria-label="Remove from cart"
        size="small"
        disabled={!user || isMinAmount || isLoading}
        onClick={() => setAmount((prev) => prev - 1)}
        sx={{
          '&:hover': {
            backgroundColor:
              !user || isMinAmount ? undefined : theme.palette.action.focus,
          },
          '&:hover svg': {
            color:
              !user || isMinAmount ? undefined : theme.palette.action.active,
          },
        }}
      >
        <RemoveIcon sx={iconStyles} />
      </IconButton>
      <TextField
        onChange={handleChangeAmount}
        value={amount}
        disabled={!user || isLoading}
        inputProps={{ style: { padding: '4px', textAlign: 'center' } }}
        sx={{ width: '35px', mx: '2px' }}
      />
      <IconButton
        aria-label="Add to cart"
        size="small"
        disabled={!user || isMaxAmount || isLoading}
        onClick={() => setAmount((prev) => prev + 1)}
        sx={{
          '&:hover': {
            backgroundColor:
              !user || isMaxAmount ? undefined : theme.palette.action.focus,
          },
          '&:hover svg': {
            color:
              !user || isMaxAmount ? undefined : theme.palette.action.active,
          },
        }}
      >
        <AddIcon sx={iconStyles} />
      </IconButton>
    </Stack>
  )
})
